import { ReactNode } from "react";
import { Box, Flex, Link, SimpleGrid, Stat, StatLabel, StatNumber, useColorModeValue } from "@chakra-ui/react";
import { typedSxMap } from "../../../utils/typedSxMap";
import { FiArrowRight, FiUsers } from "react-icons/fi";
import { GrCopy } from "react-icons/gr";
import { PiCalendarCheck } from "react-icons/pi";

interface StatsCardProps {
  title: string;
  subTitle?: string;
  icon: ReactNode;
  path: string;
  isExternal: boolean;
  comingSoon?: boolean;
}

function WelcomeLink({ title, subTitle, icon, path, isExternal, comingSoon }: StatsCardProps) {
  const styles = typedSxMap({
    stackWrapper: {
      pt: { base: "43px", sm: "30px", md: "15px", lg: "25px", xl: "30px", "2xl": "43px" },
      pb: { base: "44px", sm: "30px", md: "15px", lg: "25px", xl: "30px", "2xl": "43px" },
      px: { base: 2, sm: 4, md: 5, lg: 6, xl: 4, "2xl": 3 },
      transition: "all 0.1s ease-in-out 0s",
      _groupHover: {
        borderColor: "var(--color-primary)",
      },
      rounded: "lg",
    },
    comingSoonBorder: {
      position: "absolute",
      top: { base: "1", md: "3px", lg: "-1px" },
      left: "-9px",
      width: "0",
      height: "0",
      borderStyle: "solid",
      borderWidth: {
        base: "20px 10px 20px 0",
        sm: "20px 10px 20px 0",
        md: "14px 10px 15px 0",
        lg: "20px 10px 20px 0",
        xl: "20px 10px 20px 0",
        "2xl": "20px 10px 20px 0",
      },
      borderColor: `transparent var(--color-alert-attention) transparent transparent`,
    },
    comingSoonText: {
      position: "absolute",
      top: { base: "3px", md: "3px", lg: "-1px" },
      left: "-9px",
      backgroundColor: "var(--color-alert-attention)",
      color: "#171A20",
      padding: { base: "2px 4px",sm: "2px 4px", md: "0px 1px", lg: "2px 4px", xl: "2px 4px", "2xl": "2px 4px" },
      fontSize: { base: 14, sm: 14, md: 11, lg: 14, xl: 14 },
      fontWeight: 300,
      zIndex: "2",
      borderRadius: "5px",
    },
  });

  return (
    <Link
      role="group"
      href={path}
      textDecoration={"none"}
      isExternal={isExternal}
      backgroundColor={useColorModeValue("", "#171A20")}
      borderColor={useColorModeValue("gray.300", "")}
      borderStyle={useColorModeValue("solid", "none")}
      borderWidth={useColorModeValue("1px", "0")}
      borderRadius={"12px"}>
      <Stat
        style={{ paddingTop: "40px", paddingBottom: "42px" }}
        px={{ base: 2, md: 3 }}
        py={"5"}
        transition={"all 0.1s ease-in-out 0s"}
        _groupHover={{
          borderColor: "var(--color-primary)",
        }}
        rounded={"lg"}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          {comingSoon && <Box sx={styles.comingSoonBorder}></Box>}
          <Box mr={3}>
            {comingSoon && <Box sx={styles.comingSoonText}>Coming Soon!</Box>}
            <StatNumber fontSize={"xl"} fontWeight={"medium"}>
              {title}
            </StatNumber>
            {subTitle && (
              <Flex align="center">
                <StatLabel fontWeight={"500"} color={"#9079F9"} isTruncated>
                  {subTitle}
                </StatLabel>
                {!comingSoon && <FiArrowRight style={{ marginLeft: "5px", fontSize: "1em", color: "#9079F9" }} />}
              </Flex>
            )}
          </Box>
          <Box
            my={"auto"}
            color={useColorModeValue("gray.400", "gray.200")}
            alignContent={"center"}
            transition={"all 0.1s ease-in-out 0s"}
            _groupHover={{
              color: "var(--color-primary)",
            }}>
            {icon}
          </Box>
        </Flex>
      </Stat>
    </Link>
  );
}

export default function WelcomeBoxes() {
  return (
    <SimpleGrid columns={{ base: 1 }} spacing={{ base: 3, sm: 1, md: 1, xl: 2, lg: 1, "2xl": 3 }}>
      <WelcomeLink
        title={"Documentation"}
        subTitle={"Learn more"}
        path="https://tableflow.com/docs/"
        isExternal={true}
        icon={<GrCopy size={"35px"} />}
      />
      <WelcomeLink
        title={"Schedule a call"}
        subTitle={"Book now"}
        path="https://calendly.com/mitchpatin/30"
        isExternal={true}
        icon={<PiCalendarCheck size={"40px"} />}
      />
      <WelcomeLink title={"Invite your team"} subTitle={"Add now"} path="/users" isExternal={false} icon={<FiUsers size={"35px"} />} />
    </SimpleGrid>
  );
}
